import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StatusBadge from '@/components/ui/StatusBadge';
import Toggle from '@/components/ui/Toggle';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { usePerms } from '@/lib/perms';
import { STAFF_ROLES, useRoleLabels } from './roles';

/** Настройки → «Пользователи и роли»: сотрудники Treejar с сервера, приглашение, роль (5 ролей ТЗ п.1), флаги согласования. */
type StaffUser = {
  id: string;
  email: string;
  name: string;
  role: string;
  status: 'Active' | 'Invited' | 'Disabled';
  locale: string;
  canApproveMargin: boolean;
  canApproveLoss: boolean;
  lastLoginAt: string | null;
};

export default function UsersTab({ onToast }: { onToast: (msg: string, detail?: string, tone?: 'success' | 'error') => void }) {
  const { t, i18n } = useTranslation();
  const { me } = useAuth();
  const { can } = usePerms();
  const admin = can('users', 'write');
  const roleLabel = useRoleLabels();
  const [users, setUsers] = useState<StaffUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<string>('viewer');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-GB';

  const load = useCallback(async () => {
    setError(null);
    try {
      setUsers(await api.get<StaffUser[]>('/users'));
    } catch (e) {
      setError((e as ApiError).code);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const patch = async (u: StaffUser, body: Record<string, unknown>) => {
    try {
      await api.patch(`/users/${u.id}`, body);
      await load();
    } catch (e) {
      onToast(t('ui.error'), (e as ApiError).code, 'error');
    }
  };

  const invite = async () => {
    setBusy(true);
    setFormError('');
    try {
      const r = await api.post<StaffUser & { inviteUrl: string }>('/users/invite', { email: email.trim(), name: name.trim() || undefined, role, locale: i18n.language === 'ru' ? 'ru' : 'en' });
      setInviteUrl(r.inviteUrl);
      onToast(t('users.sent', { email: email.trim() }));
      setEmail('');
      setName('');
      await load();
    } catch (e) {
      setFormError(`${t('ui.error')}: ${(e as ApiError).code}`);
    } finally {
      setBusy(false);
    }
  };

  const reinvite = async (u: StaffUser) => {
    try {
      const r = await api.post<{ inviteUrl: string }>(`/users/${u.id}/reinvite`);
      setInviteUrl(r.inviteUrl);
      setOpen(true);
      onToast(t('users.sent', { email: u.email }));
    } catch (e) {
      onToast(t('ui.error'), (e as ApiError).code, 'error');
    }
  };

  const input = 'h-9 w-full rounded-md border border-background-300 bg-background-50 px-2.5 text-[12px] outline-none focus:border-primary-400';

  return (
    <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50 xl:col-span-2">
        <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground-950">{t('set.teamTitle')}</h2>
            <p className="mt-0.5 text-[11px] text-foreground-500">{t('set.teamSub')}</p>
          </div>
          {admin && (
            <button
              type="button"
              onClick={() => {
                setInviteUrl('');
                setOpen(true);
              }}
              className="inline-flex h-7 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-2.5 text-[11px] font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-user-add-line text-[12px]" /> {t('set.invite')}
            </button>
          )}
        </div>
        {error && (
          <p className="m-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] text-rose-700">
            {t('ui.error')}: {error}{' '}
            <button type="button" onClick={() => void load()} className="cursor-pointer font-semibold underline">
              {t('ui.retry')}
            </button>
          </p>
        )}
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full min-w-[720px] border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/70 text-left text-[10px] uppercase tracking-[0.07em] text-foreground-500">
                <th className="px-4 py-2 font-medium">{t('set.colUser')}</th>
                <th className="px-3 py-2 font-medium">{t('set.colRole')}</th>
                <th className="px-3 py-2 font-medium">{t('set.colStatus')}</th>
                <th className="px-4 py-2 font-medium">{t('set.colLast')}</th>
                {admin && <th className="px-4 py-2 font-medium">{t('common.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {!users && !error && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-foreground-400">
                    {t('ui.loading')}
                  </td>
                </tr>
              )}
              {users?.map((u) => (
                <tr key={u.id} className="border-b border-background-200/70 last:border-0">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-100 text-[10px] font-semibold text-secondary-800">
                        {(u.name || u.email)
                          .split(/[\s@.]+/)
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((n) => n[0]?.toUpperCase())
                          .join('')}
                      </span>
                      <div>
                        <p className="font-medium text-foreground-900">
                          {u.name || u.email.split('@')[0]}
                          {u.id === me?.id && <span className="ml-1 text-[10px] text-foreground-400">({t('users.you')})</span>}
                        </p>
                        <p className="text-[10.5px] text-foreground-400">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <select
                      value={u.role}
                      disabled={!admin || u.id === me?.id}
                      onChange={(e) => void patch(u, { role: e.target.value })}
                      aria-label={t('set.roleFor', { name: u.name || u.email })}
                      className="h-7 cursor-pointer rounded border border-background-200 bg-background-50 px-1.5 text-[11px] text-foreground-700 outline-none focus:border-primary-400 disabled:cursor-default"
                    >
                      {STAFF_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {roleLabel[r]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2.5">
                    <StatusBadge tone={u.status === 'Active' ? 'primary' : u.status === 'Invited' ? 'accent' : 'neutral'}>
                      {u.status === 'Disabled' ? t('users.statusDisabled') : t(`status.${u.status}`, u.status)}
                    </StatusBadge>
                  </td>
                  <td className="tnum px-4 py-2.5 text-foreground-500">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString(locale) : '—'}</td>
                  {admin && (
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap items-center gap-2 text-[10.5px] font-medium">
                        {u.status === 'Invited' && (
                          <button type="button" onClick={() => void reinvite(u)} className="cursor-pointer text-primary-700 hover:text-primary-800">
                            {t('users.reinvite')}
                          </button>
                        )}
                        {u.id !== me?.id && (
                          <button type="button" onClick={() => void patch(u, { status: u.status === 'Disabled' ? 'Active' : 'Disabled' })} className="cursor-pointer text-foreground-500 hover:text-rose-700">
                            {u.status === 'Disabled' ? t('users.enable') : t('users.disable')}
                          </button>
                        )}
                      </div>
                      <div className="mt-1 flex flex-col gap-1">
                        <Toggle checked={u.canApproveMargin} onChange={(v) => void patch(u, { canApproveMargin: v })} label={t('users.approveMargin')} />
                        <Toggle checked={u.canApproveLoss} onChange={(v) => void patch(u, { canApproveLoss: v })} label={t('users.approveLoss')} />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-4">
        <h2 className="text-sm font-semibold text-foreground-950">{t('set.rolesTitle')}</h2>
        <div className="mt-2 divide-y divide-background-200">
          {[
            [roleLabel.admin, t('set.rolesAdminDesc')],
            [roleLabel.partner_manager, t('set.rolesPartnerDesc')],
            [roleLabel.project_manager, t('set.rolesProjectDesc')],
            [roleLabel.finance, t('set.rolesFinanceDesc')],
            [roleLabel.viewer, t('set.rolesViewerDesc')],
          ].map(([r, desc]) => (
            <div key={r} className="py-2">
              <p className="text-[11.5px] font-medium text-foreground-900">{r}</p>
              <p className="text-[10.5px] leading-4 text-foreground-500">{desc}</p>
            </div>
          ))}
        </div>
        <Link to="/settings?tab=roles" className="mt-3 inline-flex items-center gap-1 text-[11.5px] font-semibold text-primary-700 hover:text-primary-800">
          {t('perm.openMatrix')} <i className="ri-arrow-right-line text-[11px]" />
        </Link>
        <p className="mt-2 text-[10.5px] leading-4 text-foreground-400">{t('perm.googleHint')}</p>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/30 p-4">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-[14px] font-semibold text-foreground-950">{t('users.inviteTitle')}</h2>
            <p className="mt-1 text-[11.5px] leading-4 text-foreground-500">{t('users.inviteSub')}</p>
            <div className="mt-3 flex flex-col gap-3">
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('users.email')}</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@treejartrading.ae" className={input} />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('users.name')}</span>
                <input value={name} onChange={(e) => setName(e.target.value)} className={input} />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('users.role')}</span>
                <select value={role} onChange={(e) => setRole(e.target.value)} className={input}>
                  {STAFF_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {roleLabel[r]}
                    </option>
                  ))}
                </select>
              </label>
              {formError && <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] text-rose-700">{formError}</p>}
              {inviteUrl && (
                <div className="rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-[11px] text-primary-800">
                  <p className="font-semibold">{t('users.inviteLink')}</p>
                  <p className="mt-1 break-all font-mono text-[10.5px]">{inviteUrl}</p>
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="h-8 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100">
                {t('common.close')}
              </button>
              <button type="button" onClick={() => void invite()} disabled={busy || !email.includes('@')} className="h-8 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60">
                {busy ? t('fb.sending') : t('users.send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
